import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import type { ProfileShortcut } from "../../../../types/profileShortcuts";
import ShortcutRow from "./ShortcutRow";

import styles from "./stylesShortcutsTable.module.css";

export interface ShortcutsTableProps {
  /** Already sorted by `order` ascending — this component does not re-sort. */
  shortcuts: ProfileShortcut[];
  /**
   * Called with the full list, in its new order, immediately after a
   * drag-and-drop reorder. The consumer owns persisting this (e.g. via the
   * `PUT /v1/profile/shortcuts/order` mutation) and rolling back on failure.
   */
  onReorder: (reordered: ProfileShortcut[]) => void;
  onToggleLive: (shortcut: ProfileShortcut, isLive: boolean) => void;
  /** Opens the create/edit modal — delete will live there too when supported. */
  onEdit: (shortcut: ProfileShortcut) => void;
  canEdit?: boolean;
}

const ShortcutsTable: React.FC<ShortcutsTableProps> = ({
  shortcuts,
  onReorder,
  onToggleLive,
  onEdit,
  canEdit = true,
}) => {
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const handleDragEnd = (event: DragEndEvent) => {
    if (!canEdit) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = shortcuts.findIndex((item) => item.id === active.id);
    const newIndex = shortcuts.findIndex((item) => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(shortcuts, oldIndex, newIndex).map((item, index) => ({
      ...item,
      order: index + 1,
    }));
    onReorder(reordered);
  };

  return (
    <div className={styles.table}>
      <div className={styles.headerRow}>
        <span className={styles.headerCell}>{AppStrings.ORDER}</span>
        <span aria-hidden="true" />
        <span className={styles.headerCell}>{AppStrings.NAME}</span>
        <span className={styles.headerCell}>{AppStrings.STATUS}</span>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={canEdit ? handleDragEnd : undefined}
      >
        <SortableContext
          items={shortcuts.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
          disabled={!canEdit}
        >
          {shortcuts.length === 0 ? (
            <div className={styles.emptyState}>{AppStrings.NO_SHORTCUTS_CONFIGURED}</div>
          ) : (
            shortcuts.map((shortcut, index) => (
              <ShortcutRow
                key={shortcut.id}
                shortcut={shortcut}
                displayOrder={index + 1}
                onToggleLive={onToggleLive}
                onEdit={onEdit}
                canEdit={canEdit}
              />
            ))
          )}
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default ShortcutsTable;
